import { Input } from "@light-technology/components";
import { type FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import { Controller, useForm } from "react-hook-form";
import { Link, useParams } from "react-router";
import { api } from "../../state/api";
import { Role, type Organization } from "../../state/api.types";
import { useAuthHasRole } from "../../state/hooks";
import { errorDetail } from "../../utilities/errors";
import { useMutationWithToast } from "../../utilities/useMutationWithToast";
import { FormCard } from "../shared/FormCard";

type TeamNameFormData = {
  name: string;
};

type TeamNameFormProps = {
  organization: Organization;
};

export const TeamNameForm = ({ organization }: TeamNameFormProps) => {
  const { appSlug } = useParams();
  const isAdmin = useAuthHasRole(Role.OrgAdmin);

  const {
    handleSubmit,
    reset,
    control,
    formState: { isDirty, isValid },
  } = useForm<TeamNameFormData>({
    defaultValues: { name: organization.name },
    mode: "onChange",
  });

  const updateOrganizationMutation = api.useUpdateOrganizationMutation();
  const [updateOrganization, updateOrganizationState] = useMutationWithToast(
    updateOrganizationMutation,
    {
      successMessage: "Team name has been updated",
    },
  );

  const onSubmit = handleSubmit(({ name }) => {
    updateOrganization({ uuid: organization.uuid, name }).then(() => {
      reset({ name });
    });
  });

  // Extract form-wide error for inline display
  const formWideError = updateOrganizationState.isError
    ? (errorDetail(updateOrganizationState.error as FetchBaseQueryError) ??
      "Team name could not be updated at this time. Please try again later.")
    : undefined;

  return (
    <FormCard
      name="Team Name"
      subtext={
        <>
          Help teammates recognize what team they've been invited to.
          {appSlug && (
            <>
              Go to{" "}
              <Link className="underline" to={`/apps/${appSlug}/appearance`}>
                Appearance
              </Link>{" "}
              to change the company name your customers see.
            </>
          )}
        </>
      }
      buttonText="Save Name"
      onButtonClick={onSubmit}
      isLoading={updateOrganizationState.isLoading}
      isDisabled={!isAdmin || !isDirty || !isValid}
      data-ph-capture-attribute-action="update-team-name"
    >
      <Controller
        control={control}
        name="name"
        rules={{ required: "Team name is required" }}
        render={({ field, fieldState }) => (
          <Input
            {...field}
            type="text"
            placeholder="Team name"
            isRequired
            validationBehavior="aria"
            isInvalid={fieldState.invalid || !!formWideError}
            hint={fieldState.error?.message ?? formWideError}
            isDisabled={!isAdmin}
          />
        )}
      />
    </FormCard>
  );
};
